"use client";

import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Heart } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { useAuthDialogStore, useFavProductStore } from "@/features/user/ui/store";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { useSession } from "@/lib/client/auth/client";
import { useShoppingCartStore } from "@/lib/client/stores";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { placeholderImage } from "@/lib/shared/constants";
import type {
  MediaDisplay,
  ProductForClient,
  ProductVariantForClient,
} from "@/lib/shared/types/interfaces";
import { translateProductVariantLabel } from "@/lib/shared/utils/productVariantLabel";
import { Button, cn, Dialog, DialogContent, DialogTitle, HStack, Text, VStack } from "@/ui";
import { ImageSlider } from "./ImageSlider";

interface QuickViewModalProps {
  product: ProductForClient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const QuickViewModal = (props: QuickViewModalProps) => {
  const { product, open, onOpenChange } = props;
  const { country } = useCountryStore();
  const generalT = useTranslations("general");
  const productDetailsT = useTranslations("product_details");

  // Variant state
  const initialVariant: ProductVariantForClient | null = product.variants?.[0] ?? null;
  const [selectedVariant, setSelectedVariant] = useState<ProductVariantForClient | null>(
    initialVariant,
  );

  // Quantity state
  const [quantity, setQuantity] = useState(1);

  // Stores
  const { setIsOpen: setCartOpen, addItem } = useShoppingCartStore();
  const { data: session } = useSession();
  const { toggleAuthDialog } = useAuthDialogStore();
  const { favProducts, addFavProduct, removeFavProduct } = useFavProductStore();

  const isFaved = useMemo(
    () => favProducts?.some((fav) => fav.id === product.id),
    [favProducts, product.id],
  );

  // Images
  const images: MediaDisplay[] = useMemo(
    () => (product.images?.length ? product.images : [placeholderImage]),
    [product.images],
  );

  // Reset state when product changes or modal opens
  useMemo(() => {
    if (open && initialVariant) {
      setSelectedVariant(initialVariant);
      setQuantity(1);
    }
  }, [open, product.id]);

  // Handlers
  const handleVariantChange = (variant: ProductVariantForClient) => {
    trackEvent(AnalyticsEvents.PRODUCT_VARIANT_SELECTED, {
      product_id: product.id,
      variant_id: variant.variant.id,
      variant_name: variant.variant.variant,
    });
    setSelectedVariant(variant);
  };

  const handleAddToCart = () => {
    if (!selectedVariant) return;

    trackEvent(AnalyticsEvents.PRODUCT_ADDED_TO_CART, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      variant_id: selectedVariant.variant.id,
      quantity,
      price: selectedVariant.onSalePrice || selectedVariant.price,
      currency: country.currencyCode,
    });
    addItem(product, selectedVariant.variant, quantity);
    setTimeout(() => setCartOpen(true), 300);
  };

  const handleWishlistToggle = () => {
    if (!session?.user) {
      toggleAuthDialog(true);
      return;
    }

    if (isFaved) {
      removeFavProduct(product);
    } else {
      addFavProduct(product);
    }
  };

  const handleViewDetails = () => {
    trackEvent(AnalyticsEvents.PRODUCT_CARD_CLICKED, {
      product_id: product.id,
      sku: product.sku,
      name: product.name,
      source: "quick_view",
    });
    onOpenChange(false);
  };

  if (!selectedVariant) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        data-testid="quick-view-dialog"
      >
        <VisuallyHidden.Root>
          <DialogTitle>{product.name}</DialogTitle>
        </VisuallyHidden.Root>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Left: Image Gallery */}
          <div>
            <ImageSlider
              images={images}
              video={product.video ?? undefined}
              width={500}
              quality={75}
              hasThumb={images.length > 1}
              canZoom={false}
              autoPlay={false}
            />
          </div>

          {/* Right: Product Info & Actions */}
          <VStack className="gap-4">
            {/* Product Name */}
            <Text className="font-product-title text-xl font-medium text-foreground">
              {product.name}
            </Text>

            {/* Price */}
            <HStack className="items-center gap-3">
              {selectedVariant.onSalePrice ? (
                <>
                  <Text className="font-price text-xl line-through text-muted-foreground">
                    {country.symbol}
                    {(selectedVariant.price * country.exchangeRate).toFixed(2)}
                  </Text>
                  <Text className="font-price text-2xl font-bold text-red-500">
                    {country.symbol}
                    {(selectedVariant.onSalePrice * country.exchangeRate).toFixed(2)}
                  </Text>
                </>
              ) : (
                <Text className="font-price text-2xl font-bold">
                  {country.symbol}
                  {(selectedVariant.price * country.exchangeRate).toFixed(2)}
                </Text>
              )}
            </HStack>

            {/* Stock status */}
            {selectedVariant.stock === 0 ? (
              <Text className="text-sm text-red-500 font-bold">{generalT("out_of_stock")}</Text>
            ) : selectedVariant.stock && selectedVariant.stock >= 10 ? (
              <Text className="text-sm font-bold text-green-600">{generalT("in_stock")}</Text>
            ) : (
              <Text className="text-sm text-foreground/75">
                {generalT("only")} {selectedVariant.stock} {generalT("left")}
              </Text>
            )}

            {/* Variants */}
            {product.variants.length > 1 && (
              <VStack className="gap-2">
                <Text className="text-sm">{generalT("variants")}</Text>
                <div className="flex flex-wrap items-center gap-2">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.variant.id}
                      onClick={() => handleVariantChange(variant)}
                      className={cn(
                        "rounded-md border px-3 py-1.5 text-sm transition-colors",
                        selectedVariant.variant.id === variant.variant.id
                          ? "border-primary bg-primary/10 font-medium"
                          : "border-border hover:border-primary/50",
                      )}
                    >
                      {translateProductVariantLabel(productDetailsT, variant.variant.variant)}
                    </button>
                  ))}
                </div>
              </VStack>
            )}

            {/* Quantity */}
            <HStack className="items-center gap-2">
              <Text className="text-sm">{generalT("quantity")}:</Text>
              <button
                disabled={quantity <= 1}
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm"
              >
                −
              </button>
              <span className="w-8 text-center text-sm">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm"
              >
                +
              </button>
            </HStack>

            {/* Action Buttons */}
            <HStack className="gap-2">
              <Button
                className="flex-1"
                disabled={selectedVariant.stock === 0}
                onClick={handleAddToCart}
              >
                {generalT("add_to_cart")}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleWishlistToggle}
                title={isFaved ? generalT("remove_from_wishlist") : generalT("add_to_wishlist")}
              >
                <Heart
                  className={cn(
                    "h-5 w-5",
                    isFaved ? "fill-red-500 text-red-500" : "text-muted-foreground",
                  )}
                />
              </Button>
            </HStack>

            {/* View Full Details Link */}
            <Button
              variant="ghost"
              className="mt-2 w-full"
              asChild
            >
              <a
                href={`/products/${product.sku}`}
                onClick={handleViewDetails}
              >
                {productDetailsT("view_full_details")}
              </a>
            </Button>
          </VStack>
        </div>
      </DialogContent>
    </Dialog>
  );
};
